import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from './useAuth';
import { Card, CreateCardDTO, AnswerDTO } from 'shared';

// Query Keys
export const cardKeys = {
  all: ['cards'] as const,
  lists: () => [...cardKeys.all, 'list'] as const,
  list: (filters: string) => [...cardKeys.lists(), { filters }] as const,
  details: () => [...cardKeys.all, 'detail'] as const,
  detail: (id: string) => [...cardKeys.details(), id] as const,
  next: () => [...cardKeys.all, 'next'] as const,
};

// Algorithm logic is now handled on the backend

// Study Flow Hooks
export const useNextCard = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: cardKeys.next(),
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');
      
      // Use the Express middleware to get next card
      return await apiClient.getNextCard();
    },
    staleTime: 0, // Always fetch fresh for study flow
    refetchOnWindowFocus: true,
    enabled: !!user,
  });
};

export const useSubmitAnswer = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ cardId, correct }: AnswerDTO) => {
      if (!user) throw new Error('User not authenticated');
      
      // Use the Express middleware to submit answer
      return await apiClient.submitAnswer({ cardId, correct });
    },
    onSuccess: () => {
      // Invalidate next card query to get a new card
      queryClient.invalidateQueries({ queryKey: cardKeys.next() });
      // Also invalidate admin cards list to show updated stats
      queryClient.invalidateQueries({ queryKey: cardKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to submit answer:', error);
    },
  });
};

// Admin Flow Hooks
export const useAllCards = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: cardKeys.lists(),
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');
      
      const response = await apiClient.getAllCards();
      return response.cards;
    },
    staleTime: 30000, // Cache for 30 seconds in admin view
    enabled: !!user,
  });
};

export const useCreateCard = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (cardData: CreateCardDTO) => {
      if (!user) throw new Error('User not authenticated');
      
      const response = await apiClient.createCard(cardData);
      return response.card;
    },
    onSuccess: () => {
      // Invalidate both admin list and next card queries
      queryClient.invalidateQueries({ queryKey: cardKeys.lists() });
      queryClient.invalidateQueries({ queryKey: cardKeys.next() });
    },
    onError: (error) => {
      console.error('Failed to create card:', error);
    },
  });
};

export const useDeleteCard = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (cardId: string) => {
      return await apiClient.deleteCard(cardId);
    },
    onSuccess: () => {
      // Invalidate both admin list and next card queries  
      queryClient.invalidateQueries({ queryKey: cardKeys.lists() });
      queryClient.invalidateQueries({ queryKey: cardKeys.next() });
    },
    onError: (error) => {
      console.error('Failed to delete card:', error);
    },
  });
};

export const useGetCard = (id: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: cardKeys.detail(id),
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');
      
      const response = await apiClient.getCard(id);
      return response.card;
    },
    enabled: !!id && !!user, // Only run query if id is provided and user is authenticated
  });
};